/**
 * Storno-Bericht — Detail-Analyse: Filiale + Kassierer parallel, mit Export.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import { ExportMenu } from '../components/ExportMenu.tsx';
import { TableSkeleton } from '../components/Skeleton.tsx';
import { fmtCurrency, fmtNumber, fmtPercent } from '../lib/format.ts';
import { Icon } from '../components/Icon.tsx';

export function StornoReportPage() {
  const filters = useFilterStore((s) => s.filters);
  const [tab, setTab] = useState<'store' | 'kassierer'>('store');

  const storeQ = useQuery({
    queryKey: ['stornos', 'store', filters],
    queryFn: () => api.stornos({ filters, groupBy: 'store' }),
  });
  const cashierQ = useQuery({
    queryKey: ['stornos', 'kassierer', filters],
    queryFn: () => api.stornos({ filters, groupBy: 'kassierer' }),
  });

  const data = tab === 'store' ? storeQ.data : cashierQ.data;
  const isLoading = tab === 'store' ? storeQ.isLoading : cashierQ.isLoading;
  const rows = [...(data ?? [])].sort((a, b) => b.storno_quote - a.storno_quote);

  // KPIs
  const sumStornos = rows.reduce((s, r) => s + r.anz_storno, 0);
  const sumVerkauf = rows.reduce((s, r) => s + r.anz_verkauf, 0);
  const totalQuote = sumStornos + sumVerkauf > 0 ? sumStornos / (sumStornos + sumVerkauf) : 0;
  const sumStornoSumme = rows.reduce((s, r) => s + r.summe_storno, 0);

  return (
    <div className="space-y-4 lg:space-y-6 w-full">
      <div>
        <div className="section-head mb-1.5">
          <span className="section-eyebrow">Bericht</span>
          <div className="section-rule" />
        </div>
        <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Storno-Analyse
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Storno-Quoten pro Filiale oder Kassierer — farbcodiert nach Risiko.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="kpi-tile">
          <div className="kpi-label">Storno-Quote gesamt</div>
          <div
            className={`kpi-value ${
              totalQuote > 0.01 ? 'kpi-delta-down' : 'kpi-delta-up'
            }`}
          >
            {fmtPercent(totalQuote, 2)}
          </div>
        </div>
        <div className="kpi-tile">
          <div className="kpi-label">Stornierte Bons</div>
          <div className="kpi-value">{fmtNumber(sumStornos)}</div>
        </div>
        <div className="kpi-tile">
          <div className="kpi-label">Storno-Summe</div>
          <div className="kpi-value">{fmtCurrency(sumStornoSumme)}</div>
        </div>
        <div className="kpi-tile">
          <div className="kpi-label">Verkaufte Bons</div>
          <div className="kpi-value">{fmtNumber(sumVerkauf)}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="inline-flex bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
            <button
              onClick={() => setTab('store')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                tab === 'store'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400'
              }`}
            >
              Pro Filiale
            </button>
            <button
              onClick={() => setTab('kassierer')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                tab === 'kassierer'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400'
              }`}
            >
              Pro Kassierer
            </button>
          </div>
          <ExportMenu
            rows={rows as unknown as Record<string, unknown>[]}
            columns={[
              { header: tab === 'store' ? 'Filiale' : 'Kassierer', key: 'label', width: 40 },
              { header: 'Verkäufe', key: 'anz_verkauf', numeric: true },
              { header: 'Stornos', key: 'anz_storno', numeric: true },
              {
                header: 'Quote',
                key: 'storno_quote',
                format: (v) => fmtPercent(Number(v), 3),
              },
              {
                header: 'Storno-Summe (€)',
                key: 'summe_storno',
                numeric: true,
                format: (v) => fmtCurrency(Number(v)),
              },
            ]}
            filename={`Storno-Analyse-${tab}`}
            title={`Storno-Analyse pro ${tab === 'store' ? 'Filiale' : 'Kassierer'}`}
          />
        </div>
        <div className="card-body">
          {isLoading ? (
            <TableSkeleton rows={12} />
          ) : rows.length === 0 ? (
            <div className="py-12 text-center">
              <Icon
                name="check"
                size={32}
                className="mx-auto mb-2 text-positive-500"
              />
              <p className="text-sm text-slate-500">
                Keine Stornos im aktuellen Filter.
              </p>
            </div>
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{tab === 'store' ? 'Filiale' : 'Kassierer'}</th>
                    <th className="num">Verkäufe</th>
                    <th className="num">Stornos</th>
                    <th className="num">Quote</th>
                    <th className="num">Storno-Summe</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const tone =
                      r.storno_quote > 0.03
                        ? 'text-accent-600 dark:text-accent-500'
                        : r.storno_quote > 0.015
                          ? 'text-warning-600 dark:text-warning-500'
                          : 'text-slate-500';
                    return (
                      <tr key={r.label}>
                        <td className="font-medium truncate max-w-xs">{r.label}</td>
                        <td className="num text-slate-500">
                          {fmtNumber(r.anz_verkauf)}
                        </td>
                        <td className={`num font-semibold ${tone}`}>
                          {fmtNumber(r.anz_storno)}
                        </td>
                        <td className={`num font-semibold ${tone}`}>
                          {fmtPercent(r.storno_quote, 2)}
                        </td>
                        <td className="num font-medium">
                          {fmtCurrency(r.summe_storno)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
